import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { plus } from "@/math/coordinates";
import type { SizedRectangle, WindowLen } from "@/model/coordinates";
import { windowLen } from "@/model/coordinates";
import { asReadonly } from "@/utils/vue";

export const useBoardSizeStore = defineStore("boardSize", () => {
  // effective, calculated board pos/size
  const board = ref({ p0: windowLen(0, 0), size: windowLen(0, 0) });

  // scrollable margins around the board
  const scrollableMargin = ref({ p0: windowLen(0, 0), p1: windowLen(0, 0) });

  // visible margins around the board when zoomed to 100%
  const visibleMargin = ref({ p0: windowLen(64, 16), p1: windowLen(64, 64) });

  // margins of the window, unavailable for the board
  const windowMargin = ref({ p0: windowLen(0, 48), p1: windowLen(0, 0) });

  // when a side panel is open, scrollableMargins must stay
  // otherwise, we are allowed to make it smaller if possible
  // (per side, so closing one panel does not release the other's margin)
  const marginLockedSides = ref({ left: false, right: false });

  return {
    board: asReadonly(board),
    windowMargin: asReadonly(windowMargin),
    scrollableMargin: asReadonly(scrollableMargin),
    scrollableMarginLocked: computed(
      () => marginLockedSides.value.left || marginLockedSides.value.right,
    ),
    marginLockedSides: asReadonly(marginLockedSides),
    lockScrollableMargin: (side: "left" | "right", locked: boolean) =>
      (marginLockedSides.value[side] = locked),

    leftTopMargin: computed(() =>
      plus(plus(scrollableMargin.value.p0, visibleMargin.value.p0), windowMargin.value.p0),
    ),
    rightBottomMargin: computed(() =>
      plus(plus(scrollableMargin.value.p1, visibleMargin.value.p1), windowMargin.value.p1),
    ),
    totalScrollableMargin: computed(() =>
      plus(scrollableMargin.value.p0, scrollableMargin.value.p1),
    ),
    totalVisibleMargin: computed(() => plus(visibleMargin.value.p0, visibleMargin.value.p1)),
    totalWindowMargin: computed(() => plus(windowMargin.value.p0, windowMargin.value.p1)),

    setBoard: (sized: SizedRectangle) => (board.value = sized),
    addScrollableLeftTop: (margin: WindowLen) =>
      (scrollableMargin.value.p0 = plus(scrollableMargin.value.p0, margin)),
    addScrollableRightBottom: (margin: WindowLen) =>
      (scrollableMargin.value.p1 = plus(scrollableMargin.value.p1, margin)),
    resetScrollableMargin: () => {
      scrollableMargin.value.p0 = windowLen(0, 0);
      scrollableMargin.value.p1 = windowLen(0, 0);
    },
  };
});
